import React, { useCallback } from "react";
import * as Scrivito from "scrivito";
import { Button } from "components";
import { useTranslate } from "hooks";
import {
  useIsProductLoading,
  useProductActions,
  useProductNodeId,
} from "stores/product";
import NiceModal from "@ebay/nice-modal-react";
import { ProductAddingModal } from "components";
import { useSetCopiedItem, useProject } from "stores/project";
import { toast } from "react-toastify";
import type { NodeApi } from "@hogartrabe/react-arborist-vrh";
import { isAnyAccessory } from "utils/treeUtils";
import { NodeKinds } from "interfaces/enums";
import type { Node } from "interfaces/schemas";

export const AddToProjectButton = () => {
  const translate = useTranslate();
  const isProductLoading = useIsProductLoading();
  const setCopiedItem = useSetCopiedItem();
  const productActions = useProductActions();
  const productId = useProductNodeId();
  const project = useProject();
  const { kind: kindParam } = Scrivito.currentPageParams();
  const kindString = typeof kindParam === "string" ? kindParam : undefined;
  const kind = getNodeKindFromString(kindString);

  const isDisabled =
    isProductLoading ||
    !!(!!project?.id && kind && isAnyAccessory({ kind } as Node));

  const onClick = useCallback(() => {
    const productNode = productActions.toProductNode();
    if (!productNode) {
      toast.error(translate("PRODUCT_CONFIGURATOR_ADD_TO_PROJECT_ERROR"));
      return;
    }
    const productNodeApi = {
      data: productNode,
    } as NodeApi; // We only need to pass the data property to the copied item, so we can type it as NodeApi with a data property. The rest of the properties are not needed for the copied item and will not be used in the ProductAddingModal.
    setCopiedItem(productNodeApi);
    NiceModal.show(ProductAddingModal);
  }, [productActions, setCopiedItem, translate]);

  return (
    <Button
      data-testid={`product-configurator-header-add-to-${productId ? "other-project" : "project"}-btn`}
      color={productId ? "secondary" : "primary"}
      onClick={onClick}
      disabled={isDisabled}
    >
      {translate(
        productId
          ? "PRODUCT_CONFIGURATOR_ADD_TO_OTHER_PROJECT"
          : "ADD_TO_PROJECT",
      )}
    </Button>
  );
};

const getNodeKindFromString = (kindStr: string | undefined) => {
  if (!kindStr) return null;
  const kindKey = Object.keys(NodeKinds).find(
    (key) => key.toLowerCase() === kindStr.toLowerCase(),
  );
  return kindKey ? NodeKinds[kindKey as keyof typeof NodeKinds] : null;
};
